import { Property } from '@/api/types/categories.types';

function extractSelectedOptionIds(value: unknown): string[] {
  if (value === undefined || value === null) return [];
  if (typeof value === 'string') {
    if (!value) return [];
    return value
      .split(',')
      .map(part => part.trim())
      .filter(Boolean);
  }
  if (Array.isArray(value)) {
    return value.flatMap(extractSelectedOptionIds).filter(Boolean);
  }
  if (typeof value === 'object' && 'legacyResourceId' in value) {
    const id = String((value as { legacyResourceId: string }).legacyResourceId);
    return id ? [id] : [];
  }
  return [];
}

export function getVisibleAndHiddenProperties(
  properties: Property[],
  collectionValues: Record<string, unknown>
): { visibleProperties: Property[]; hiddenPropertyIds: string[] } {
  const hiddenPropertyIds = getHiddenPropertyIds(properties, collectionValues);
  if (!hiddenPropertyIds.length) {
    return { visibleProperties: properties, hiddenPropertyIds };
  }
  const hiddenSet = new Set(hiddenPropertyIds);
  const visibleProperties = properties.filter(
    p => !hiddenSet.has(p.legacyResourceId)
  );
  return { visibleProperties, hiddenPropertyIds };
}

export function filterVisibleProperties(
  properties: Property[],
  collectionValues: Record<string, unknown>
): Property[] {
  const hiddenIds = getHiddenPropertyIds(properties, collectionValues);
  if (!hiddenIds.length) return properties;
  const hiddenIdsSet = new Set(hiddenIds);
  return properties.filter(p => !hiddenIdsSet.has(p.legacyResourceId));
}

export function getHiddenPropertyIds(
  properties: Property[],
  collectionValues: Record<string, unknown>
): string[] {
  const directlyHiddenIds = properties
    .filter(p => isHidden(p, collectionValues))
    .map(p => p.legacyResourceId);
  return expandWithDescendants(properties, directlyHiddenIds);
}

function isHidden(
  property: Property,
  collectionValues: Record<string, unknown>
): boolean {
  if (!property.hiddenWhen) return false;
  const conditions = Array.isArray(property.hiddenWhen)
    ? property.hiddenWhen
    : [property.hiddenWhen];
  return conditions.some(condition => {
    const selectedOptionIds = extractSelectedOptionIds(
      collectionValues[condition.propertyLegacyId]
    );
    if (!selectedOptionIds.length) return false;
    return selectedOptionIds.every(id =>
      condition.optionLegacyIds.includes(id)
    );
  });
}

function expandWithDescendants(
  properties: Property[],
  ids: string[]
): string[] {
  if (!ids.length) return ids;
  const hiddenSet = new Set(ids);
  const children = properties
    .filter(p => p.parentLegacyId && hiddenSet.has(p.parentLegacyId))
    .map(p => p.legacyResourceId);
  if (!children.length) return ids;
  return Array.from(
    new Set([...ids, ...expandWithDescendants(properties, children)])
  );
}

export function stripHiddenPropertyParams(
  searchParams: URLSearchParams,
  hiddenIds: string[]
): URLSearchParams {
  const stripped = new URLSearchParams(searchParams);
  for (const id of hiddenIds) {
    stripped.delete(`p[${id}]`);
    stripped.delete(`p[${id}][]`);
  }
  return stripped;
}

export function buildCleanUrlWithPrunedParams(
  currentUrl: string,
  hiddenIds: string[]
): string {
  if (!hiddenIds.length) return currentUrl;
  const baseParams = new URL(currentUrl);
  const cleanedParams = stripHiddenPropertyParams(
    baseParams.searchParams,
    hiddenIds
  );
  baseParams.search = cleanedParams.toString();
  return baseParams.href;
}

export function pruneHiddenProperties<T extends Record<string, unknown>>(
  collection: T,
  hiddenIds: string[]
): T {
  if (!hiddenIds.length) return collection;
  const hiddenSet = new Set(hiddenIds);
  return Object.fromEntries(
    Object.entries(collection).filter(([key]) => !hiddenSet.has(key))
  ) as T;
}

export function pruneHiddenPropertyCollections<
  T extends {
    collection?: Record<string, unknown>;
    range?: Record<string, unknown>;
    boolean?: Record<string, unknown>;
  }
>(collections: T, hiddenIds: string[]): T {
  if (!hiddenIds.length) return collections;
  return {
    ...collections,
    ...(collections.collection && {
      collection: pruneHiddenProperties(collections.collection, hiddenIds),
    }),
    ...(collections.range && {
      range: pruneHiddenProperties(collections.range, hiddenIds),
    }),
    ...(collections.boolean && {
      boolean: pruneHiddenProperties(collections.boolean, hiddenIds),
    }),
  };
}

export function pruneHiddenPropertyOptions<T extends { legacyId: number }>(
  options: T[],
  hiddenIds: string[]
): T[] {
  if (!hiddenIds.length) return options;
  const hiddenSet = new Set(hiddenIds);
  return options.filter(option => !hiddenSet.has(String(option.legacyId)));
}
